import clsx from 'clsx';
import { LabelsType } from '../types';
import { useRef, useState } from 'react';

type Props = {
    currentYear: number;
    graphData: { year: number; totalValue: number; deposits: number }[];
    years: number;
    getLabel: (key: keyof LabelsType, replacements?: Record<string, string | number>) => string;
    showRelativeNumberOfYears?: boolean;
};

export const SavingsGrowthGraph = ({ currentYear, graphData, years, getLabel, showRelativeNumberOfYears }: Props) => {
    const [tooltip, setTooltip] = useState<null | {
        pointX: number;
        pointY: number;
        label: number;
        year: number;
        value: number;
    }>(null);
    const pathRef = useRef<SVGPathElement | null>(null);

    if (!graphData.length) return null;

    const width = 600;
    const height = 400;
    const padding = { top: 60, right: 26, bottom: 60, left: 80 };
    const graphWidth = width - padding.left - padding.right;
    const graphHeight = height - padding.top - padding.bottom;

    const maxValue = Math.max(...graphData.map(d => Math.max(d.totalValue, d.deposits)), 500000);
    const maxYears = years;

    const xScale = (year: number) => (year / maxYears) * graphWidth + padding.left;
    const yScale = (value: number) => height - padding.bottom - (value / maxValue) * graphHeight;

    const totalValuePath = graphData.map((point, i) => `${i === 0 ? 'M' : 'L'} ${xScale(point.year)} ${yScale(point.totalValue)}`).join(' ');

    const depositsPath = graphData.map((point, i) => `${i === 0 ? 'M' : 'L'} ${xScale(point.year)} ${yScale(point.deposits)}`).join(' ');

    // Format Y axis label
    const formatYLabel = (value: number) => `${Math.round(value / 1000)} k`;

    // Generate Y-axis labels (3 evenly spaced)
    const yAxisLabels = Array.from({ length: 4 }, (_, i) => {
        const value = (maxValue / 3) * i;
        return { value, y: yScale(value) };
    });

    // Generate X-axis labels (based on years, show max 4 labels)
    const xAxisStep = Math.ceil(maxYears / 3);
    const xAxisLabels = Array.from({ length: Math.ceil(maxYears / xAxisStep) + 1 }, (_, i) => {
        const year = i * xAxisStep;

        return year <= maxYears ? { year, x: xScale(year) } : null;
    }).filter(Boolean);

    const onMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
        const svg = e.currentTarget;
        const path = pathRef.current;
        if (!svg || !path) return;

        const rect = svg.getBoundingClientRect();

        // Convert mouse px → SVG coordinate system
        const scaleX = svg.viewBox.baseVal.width / rect.width;
        const scaleY = svg.viewBox.baseVal.height / rect.height;

        const mouseX = (e.clientX - rect.left) * scaleX;
        const mouseY = (e.clientY - rect.top) * scaleY;

        const totalLength = path.getTotalLength();

        let closestPoint = null;
        let minDist = Infinity;

        const samples = 300;

        for (let i = 0; i <= samples; i++) {
            const p = path.getPointAtLength((i / samples) * totalLength);
            const dx = p.x - mouseX;
            const dy = p.y - mouseY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < minDist) {
                minDist = dist;
                closestPoint = p;
            }
        }

        const threshold = 8;

        if (minDist > threshold) {
            setTooltip(null);
            return;
        }

        // closest real data point
        let closestData = graphData[0];
        let smallestDx = Infinity;
        for (const d of graphData) {
            const px = xScale(d.year);
            const diff = Math.abs(px - (closestPoint?.x || 0));
            if (diff < smallestDx) {
                smallestDx = diff;
                closestData = d;
            }
        }

        setTooltip({
            pointX: closestPoint?.x || 0,
            pointY: closestPoint?.y || 0,
            label: Math.round(closestData.totalValue / 1000),
            year: closestData.year,
            value: Math.round(closestData.totalValue),
        });
    };

    return (
        <>
            <svg
                viewBox={`0 0 ${width} ${height}`}
                className="-r-ml-2 r-h-auto r-w-full md:-r-ml-3"
                style={{ maxHeight: '400px' }}
                onMouseMove={onMouseMove}
                onMouseLeave={() => setTooltip(null)}
                role="img"
                aria-labelledby="chartTitle chartDesc"
            >
                <title id="chartTitle" className="r-sr-only">
                    {getLabel('savingsGrowthOverTime')}
                </title>
                <desc id="chartDesc" className="r-sr-only">
                    {getLabel('graphDesc')}
                </desc>

                {yAxisLabels.map((label, i) => (
                    <line
                        key={`grid-${i}`}
                        x1={padding.left}
                        y1={label.y}
                        x2={width - padding.right}
                        y2={label.y}
                        stroke={i === 0 ? '#000' : '#B8B8B8'}
                        strokeWidth={i === 0 ? '2' : '1'}
                    />
                ))}

                {/* Y-axis labels */}
                {yAxisLabels.map((label, i) => (
                    <text
                        key={`y-label-${i}`}
                        x={padding.left - 10}
                        y={label.y}
                        textAnchor="end"
                        alignmentBaseline="middle"
                        className={clsx('r-fill-gray-600 r-text-lg r-font-bold', { 'r-opacity-0': i === 0 })}
                    >
                        {formatYLabel(label.value)}
                    </text>
                ))}

                {/* X-axis labels */}
                {xAxisLabels.map(
                    label =>
                        label && (
                            <text
                                key={`x-label-${label.year}`}
                                x={label.x}
                                y={height - padding.bottom + 25}
                                textAnchor="middle"
                                className="r-fill-gray-600 r-text-lg r-font-bold"
                            >
                                {label.year === 0
                                    ? getLabel('today')
                                    : showRelativeNumberOfYears
                                      ? `${getLabel('year').charAt(0).toUpperCase()}${getLabel('year').slice(1)} ${label.year}`
                                      : `${currentYear + label.year}`}
                            </text>
                        )
                )}

                {/* Total value line (compound interest) */}
                <path
                    ref={pathRef}
                    d={totalValuePath}
                    fill="none"
                    stroke="rgb(76,61,55)"
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />

                {/* Deposits line (straight line) */}
                <path
                    d={depositsPath}
                    fill="none"
                    stroke="#18968b"
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray="8,8"
                />

                {tooltip && (
                    <g transform={`translate(${tooltip.pointX - 22}, ${tooltip.pointY - 10})`}>
                        <circle cx={22} cy={10} r={12} fill="white" stroke="rgb(76,61,55)" strokeWidth={3} />
                        <rect x={-58} y={-42} width={74} height={38} rx={20} fill="rgb(76,61,55)" />
                        <text
                            x={-22}
                            y={-18}
                            textAnchor="middle"
                            fill="white"
                            style={{
                                fontSize: '1rem',
                                fontWeight: 700,
                            }}
                        >
                            {tooltip.label} k
                        </text>
                    </g>
                )}
            </svg>

            <div className="mt-2 md:mt-0 r-flex r-flex-row r-justify-center r-gap-4 r-px-2 md:r-mb-4 md:r-justify-start md:r-px-6">
                <div className="r-flex r-items-center r-gap-2 md:r-gap-4">
                    <div className="r-size-4 r-rounded-full r-bg-[#18968b]" />
                    <span className="r-text-gray-700">{getLabel('depositAmount')}</span>
                </div>
                <div className="r-flex r-items-center r-gap-2 md:r-gap-4">
                    <div className="r-size-4 r-rounded-full r-bg-[rgb(76,61,55)]" />
                    <span className="r-font-bold r-text-gray-700">{getLabel('finalAmount')}</span>
                </div>
            </div>
        </>
    );
};
